import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Check, Star, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import LeadMagnetForm from "../components/LeadMagnetForm";
import { Reveal } from "../components/Reveal";
import client from "../lib/api";
import { trackEvent } from "../lib/tracking";

const HERO_BULLETS = [
  "12 pagine pratiche, dritte al punto",
  "La ricevi subito via email, gratis",
  "Scritta da Stefano Cremonese, L'Influexpert del Vino",
  "Zero snobismi: consigli che usi già da stasera",
];

const LEARN = [
  "La temperatura giusta per ogni vino (quasi tutti la sbagliano)",
  "Il bicchiere che cambia il sapore di ciò che bevi",
  "Come capire se un vino è \"andato\" prima di versarlo",
  "Gli abbinamenti che esaltano piatto e bottiglia",
  "L'errore n.1 quando apri una bottiglia importante",
  "Come leggere un'etichetta senza farti fregare",
];

function FormCard({ testId }) {
  return (
    <div data-testid={testId} className="rounded-3xl bg-panna p-6 sm:p-8 shadow-2xl shadow-black/50 border border-oro/20">
      <div className="inline-flex items-center gap-2 rounded-full bg-bordeaux/10 px-3.5 py-1.5 text-bordeaux text-xs font-semibold uppercase tracking-wider">
        <Gift size={14} /> Scaricala gratis ora
      </div>
      <h3 className="mt-4 font-serif text-2xl font-bold text-nero leading-tight">
        Ricevi subito la guida in PDF
      </h3>
      <p className="mt-2 text-nero/60 text-sm">Lascia nome ed email: te la mandiamo all'istante.</p>
      <div className="mt-5">
        <LeadMagnetForm dark={false} source="lancio" />
      </div>
      <p className="mt-4 text-nero/45 text-[11px]">Gratis · niente spam · disiscrizione con un clic</p>
    </div>
  );
}

export default function Lancio() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    trackEvent("view_lancio");
    client.get("/leads/count").then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  return (
    <div data-testid="lancio-page" className="bg-nero text-panna grain">
      <SEO
        title="Guida gratuita al vino — 7 errori comuni | Dentro il Calice"
        path="/lancio"
        description="Scarica gratis la guida in PDF '7 errori comuni quando bevi vino' di Stefano Cremonese. 12 pagine pratiche, senza snobismi, subito via email."
      />

      {/* HERO — modulo subito visibile */}
      <section className="relative min-h-screen flex items-center overflow-hidden py-24 lg:py-0">
        <div className="absolute inset-0">
          <img src="/assets/lancio-hero.jpg" alt="" className="h-full w-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-nero via-nero/90 to-nero/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-nero via-transparent to-nero/50" />
        </div>

        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Sinistra: promessa + benefici */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-oro text-xs sm:text-sm font-semibold uppercase tracking-[0.3em]"
            >
              Guida gratuita · PDF
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]"
            >
              7 errori comuni<br /><span className="text-oro">quando bevi vino</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
              className="mt-5 text-panna/80 text-base sm:text-lg leading-relaxed max-w-lg"
            >
              La guida gratuita di Stefano Cremonese per smettere di sbagliare e iniziare a
              goderti davvero ogni calice. Semplice, pratica, senza snobismi.
            </motion.p>

            <motion.ul
              initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-7 space-y-2.5"
            >
              {HERO_BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-panna/90 text-sm sm:text-base">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-oro/20 text-oro">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-7 flex items-center gap-3"
            >
              <div className="flex text-oro">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} fill="currentColor" strokeWidth={0} />)}
              </div>
              <span data-testid="lancio-social-proof" className="text-panna/70 text-sm">
                Già scaricata da <strong className="text-panna">oltre {count ?? "200"}</strong> appassionati
              </span>
            </motion.div>
          </div>

          {/* Destra: modulo (in evidenza) */}
          <motion.div
            id="guida"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
          >
            <FormCard testId="lancio-hero-form" />
          </motion.div>
        </div>
      </section>

      {/* COSA IMPARERAI — nessun link in uscita, solo valore */}
      <section className="py-20 sm:py-24 border-y border-panna/10" data-testid="lancio-learn">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <p className="text-oro text-xs font-semibold uppercase tracking-[0.25em] text-center">Cosa c'è dentro</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-bold text-center max-w-2xl mx-auto">
              Cosa scoprirai nella guida
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
            {LEARN.map((item, i) => (
              <Reveal key={item} delay={i * 0.05}>
                <div className="flex items-start gap-4 rounded-2xl border border-panna/10 bg-nero-light/40 p-5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-oro/40 text-oro text-sm font-serif">
                    {i + 1}
                  </span>
                  <p className="text-panna/80 text-sm sm:text-base leading-relaxed">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="bg-panna py-16 sm:py-20" data-testid="lancio-quote">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <p className="font-serif text-2xl sm:text-3xl italic text-bordeaux leading-relaxed">
            "Il vino è fatto dall'uomo: dietro ogni calice c'è una storia."
          </p>
          <p className="mt-5 text-nero/60 text-sm uppercase tracking-[0.2em]">Stefano Cremonese</p>
        </div>
      </section>

      {/* CTA FINALE — seconda occasione per convertire */}
      <section className="bg-bordeaux py-20 sm:py-24" data-testid="lancio-cta-finale">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-panna leading-tight">
              Il tuo primo assaggio di Dentro il Calice
            </h2>
            <p className="mt-5 text-panna/85 leading-relaxed">
              Lascia nome ed email e ricevi <strong className="text-panna">subito e gratis</strong> la guida in PDF.
              È il modo migliore per iniziare a bere meglio già da stasera — poi ci pensiamo noi a raccontarti il resto.
            </p>
            <p className="mt-6 inline-flex items-center gap-2 text-oro text-sm">
              <ArrowRight size={16} /> Nessun costo, nessun impegno.
            </p>
          </div>
          <FormCard testId="lancio-cta-form" />
        </div>
      </section>
    </div>
  );
}
